import { formatBackupTime } from "@/lib/format";
import { Badge, healthLabel, healthToBadgeVariant } from "@/components/ui/Badge";
import { RuntimeDoctorStatus } from "@aisecure/status/ui";
import { LABEL } from "@aisecure/status";
import type { TenantHealthRow } from "@/types";

interface TenantHealthTableProps {
  tenants: TenantHealthRow[];
  loading?: boolean;
  onSelectTenant: (tenant: TenantHealthRow) => void;
}

function versionCell(value: string | null | undefined): string {
  const text = (value ?? "").trim();
  return text || LABEL.notAvailable;
}

export function TenantHealthTable({ tenants, loading, onSelectTenant }: TenantHealthTableProps) {
  if (loading) {
    return (
      <div className="glass-panel attention-empty" aria-busy="true">
        Loading tenants from Control Plane…
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="glass-panel attention-empty">
        No data available — no tenants registered in Control Plane.
      </div>
    );
  }

  return (
    <div className="glass-panel">
      <div className="data-table-wrap">
        <table className="data-table" aria-label="Tenant health">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Health</th>
              <th>Runtime Doctor</th>
              <th>Platform version</th>
              <th>AI version</th>
              <th>Last backup</th>
              <th>Open alerts</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr
                key={tenant.slug}
                className="data-table__row--clickable"
                onClick={() => onSelectTenant(tenant)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectTenant(tenant);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Open ${tenant.displayName} Tenant Control Center`}
              >
                <td>
                  <span className="data-table__link">{tenant.displayName}</span>
                  <div className="data-table__sub">{tenant.slug}</div>
                </td>
                <td>
                  <Badge variant={healthToBadgeVariant(tenant.status)}>
                    {healthLabel(tenant.status)}
                  </Badge>
                </td>
                <td>
                  <RuntimeDoctorStatus status={tenant.doctorStatus} />
                </td>
                <td>{versionCell(tenant.platformVersion)}</td>
                <td>{versionCell(tenant.aiVersion)}</td>
                <td>{formatBackupTime(tenant.lastBackup)}</td>
                <td>
                  {tenant.openAlerts > 0 ? (
                    <Badge variant={tenant.openAlerts >= 2 ? "danger" : "warning"}>
                      {tenant.openAlerts}
                    </Badge>
                  ) : (
                    <span className="data-table__muted">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
